/*
  Shopify Boilerplate - Main JS Entry Point
  ES Modules + Web Components approach
*/

// =============================================================================
// Core imports — loaded on every page (header, nav, cart, images)
// =============================================================================
import './lazy-image.js';
import './reveal-image.js';
import './quantity-input.js';
import './cart-drawer.js';
import './mobile-nav.js';
import './header-nav.js';
import './predictive-search.js';

import { cartAPI } from './cart-api.js';
window.cartAPI = cartAPI;

// =============================================================================
// Lazy imports — only loaded when the element exists on the page
// To revert: uncomment the static imports below and delete the lazy block
//
// import './collection-filters.js';
// import './collection-slider.js';
// import './product-form.js';
// import './product-switcher.js';
// import './product-gallery.js';
// import './pdp-configurator.js';
// import './tab-group.js';
// import './product-recommendations.js';
// import './hero-slideshow.js';
// import './login-toggle.js';
// import './address-forms.js';
// import './portal-products.js';
// import './dealer-finder.js';
// import './form-submit.js';
// =============================================================================
const lazyComponents = [
  ['collection-filters', './collection-filters.js'],
  ['collection-slider', './collection-slider.js'],
  ['product-form', './product-form.js'],
  ['product-switcher', './product-switcher.js'],
  ['product-gallery', './product-gallery.js'],
  ['pdp-configurator', './pdp-configurator.js'],
  ['tab-group', './tab-group.js'],
  ['product-recommendations', './product-recommendations.js'],
  ['hero-slideshow', './hero-slideshow.js'],
  ['login-toggle', './login-toggle.js'],
  ['address-forms', './address-forms.js'],
  ['portal-products', './portal-products.js'],
  ['dealer-finder', './dealer-finder.js'],
  ['hubspot-form', './form-submit.js'],
  ['social-widget', './social-widget.js'],
  ['pdp-vehicle-select', './pdp-vehicle-select.js'],
];

for (const [tag, path] of lazyComponents) {
  if (document.querySelector(tag)) import(path);
}

// Scroll animations — observe [data-stagger] and [data-fade-up], add .is-visible on entry
const scrollObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      scrollObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.15 });
document.querySelectorAll('[data-stagger], [data-fade-up]').forEach(el => scrollObserver.observe(el));

// Global DOM ready functionality
document.addEventListener('DOMContentLoaded', () => {
  // Remove 'no-js' class for progressive enhancement
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');
  
  // Focus management for accessibility
  setupFocusManagement();
  
  // Setup global keyboard navigation
  setupKeyboardNavigation();
});

/**
 * Focus management for accessibility
 */
function setupFocusManagement() {
  // Skip to content link
  const skipLink = document.querySelector('.skip-to-content-link');
  if (skipLink) {
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(skipLink.getAttribute('href'));
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }
  
  // Focus trap for modals/drawers (handled by individual components)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Let components handle their own escape key behavior
      const event = new CustomEvent('escape-pressed');
      document.dispatchEvent(event);
    }
  });
}

/**
 * Keyboard navigation helpers
 */
function setupKeyboardNavigation() {
  // Arrow key navigation for product grids
  document.addEventListener('keydown', (e) => {
    if (e.target.matches('[data-product-grid] .product-card')) {
      const grid = e.target.closest('[data-product-grid]');
      const cards = Array.from(grid.querySelectorAll('.product-card'));
      const currentIndex = cards.indexOf(e.target);
      
      let nextIndex;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = (currentIndex + 1) % cards.length;
          cards[nextIndex].focus();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = currentIndex === 0 ? cards.length - 1 : currentIndex - 1;
          cards[nextIndex].focus();
          break;
      }
    }
  });
}

/**
 * Utility function for debouncing
 * Used by various components for performance
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Utility function for throttling
 * Used for scroll events, resize events, etc.
 */
export function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Custom event dispatcher for component communication
 */
export function dispatchCustomEvent(element, eventName, detail = {}) {
  const event = new CustomEvent(eventName, {
    detail,
    bubbles: true,
    cancelable: true
  });
  element.dispatchEvent(event);
}

